/**
 * The data shape NovaInsightWidgetComponent / NovaInsightModalComponent render.
 * Ported from the confirmed design handoff (`nova-insights/insight-card/insight-card.types.js`,
 * FLIP-169) — see that file's category-by-category trigger definitions for the source of truth
 * on when each category applies; this is just the rendering shape.
 */
export interface InsightCard {
  /** One of INSIGHT_CATEGORIES below. */
  category: string;
  /** 3-8 words. Shown in the collapsed widget. */
  snackbarTitle: string;
  /** One sentence. Shown in the collapsed widget. */
  snackbarSentence: string;
  /** Max 8 words. Falls back to snackbarTitle if empty. */
  expandedTitle: string;
  /** "What Nova Noticed" - two sentences. */
  noticed: string;
  /** "Why This Might Matter" - max two sentences. */
  matters: string;
  /** "A Question Worth Exploring" - one question. */
  question: string;
  /** Optional CTA label, e.g. "Open Sanctuary", "Review Score". Omit/empty to hide the CTA. */
  action?: string;

  /**
   * For the "Explore" action only: the id of the Learn lesson this card points at, or omitted/empty
   * for none (FLIP-224).
   *
   * Resolved server-side from a lesson TITLE the model named, against that user's own digest — so
   * by the time it reaches here it is either a lesson that really existed at generation time or
   * empty. It can still go stale: lesson ids are reassigned on a content reseed, and a card lives
   * in a cache for hours. LearnShellComponent already handles that, ignoring a `lesson` query param
   * it cannot find, which leaves the user on the Learn homepage.
   */
  actionTargetId?: string;
}

/**
 * The confirmed set of Insight Card categories (12, down from an original 15 - see
 * insight-card.types.js for what was cut and why). RECOVERY_FROM_RED's copy must never use the
 * word "recovery" - see insight-generation-prompt.js's language rules in the handoff.
 */
export const INSIGHT_CATEGORIES = [
  "BUFFER_SHIFT",
  "SHADOW_CLUSTER",
  "STAR_CLUSTER",
  "PAIN_DOMINANT_WEEK",
  "SAFETY_CARD_GAP",
  "RECOVERY_FROM_RED",
  "QUIET_PROGRESS",
  "SANCTUARY_MOMENT",
  "MINI_SAFETY_PLAN_OPPORTUNITY",
  "COMMUNITY_INVITATION",
  "NOVA_NOTICED",
] as const;

/**
 * The one CTA label that can carry a lesson target. Every other label maps to a fixed route with
 * no notion of a target — see InsightCard.actionTargetId.
 */
export const EXPLORE_ACTION = "Explore";

/** Maps action CTA labels to their navigation routes. */
export const INSIGHT_ACTION_ROUTES: Record<string, string> = {
  "Open Sanctuary": "/sanctuary",
  "Review Score": "/score",
  "Add a Safety Card": "/plan-home",
  "Visit Community": "/community",
  Explore: "/learn",
};

/** Default/empty state - used before the first real card loads. */
export const EMPTY_INSIGHT_CARD: InsightCard = {
  category: "NOVA_NOTICED",
  snackbarTitle: "Nova noticed something",
  snackbarSentence: "",
  expandedTitle: "",
  noticed: "",
  matters: "",
  question: "",
  action: "",
  actionTargetId: "",
};
